import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mountain, Bell, ChevronDown, CircleHelp, LogOut, Map, Building2, Sparkles, Compass, Route, CreditCard, Plane, Settings } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { openSupportEmail, supportEmailHref } from "@/lib/supportEmail";

function useOutside(cb) {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) cb(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [cb]);
  return ref;
}

function OrganizationBrandMark({ logo }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => { setFailed(false); }, [logo]);

  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-[#E5B85C] text-[#17211D]" aria-hidden="true">
      {logo && !failed
        ? <img src={logo} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
        : <Mountain className="h-5 w-5" />}
    </span>
  );
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const ref = useOutside(() => setOpen(false));

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/notifications");
      setItems(data.data || []); setUnread(data.unread || 0);
    } catch (err) {
      setError(formatApiError(err, "Could not load notifications."));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const readAll = async () => {
    try { await api.post("/notifications/read-all"); await load(); }
    catch (err) { toast.error(formatApiError(err, "Could not mark notifications read.")); }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell-button" onClick={() => { setOpen(!open); if (!open) load(); }}
        type="button"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="relative min-h-10 min-w-10 rounded-2xl border border-[#17211D]/10 bg-[#FFFAF1] p-2 text-[#254F43] shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] focus-visible:ring-offset-2">
        <Bell className="h-5 w-5" aria-hidden="true" />
        {unread > 0 && (
          <span data-testid="notification-unread-count"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#2D7A65] px-1 text-[10px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel"
          role="dialog"
          aria-label="Notifications"
          className="tp-paper absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-3xl">
          <div className="flex items-center justify-between border-b border-[#17211D]/10 px-4 py-2.5">
            <span className="text-sm font-semibold text-[#1A2420]">Notifications</span>
            {unread > 0 && <button type="button" onClick={readAll} data-testid="notif-read-all" className="min-h-8 rounded-xl px-2 text-xs font-bold text-[#2D7A65] hover:bg-[#E5B85C]/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C]">Mark all read</button>}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <p className="px-4 py-8 text-center text-sm text-slate-400">Loading notifications...</p>
            ) : error ? (
              <div className="px-4 py-6 text-center text-sm text-rose-600">
                <p>{error}</p>
                <button type="button" onClick={load} className="mt-2 min-h-10 rounded px-3 font-medium text-[#2D7A65] hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]">Retry</button>
              </div>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-400">No notifications yet</p>
            ) : items.map((n) => (
              <div key={n.id} data-testid="notification-item"
                className={`border-b border-[#17211D]/10 px-4 py-3 ${n.read_at ? "opacity-60" : "bg-[#E5B85C]/10"}`}>
                <p className="text-sm font-medium text-[#1A2420]">{n.title}</p>
                <p className="text-xs text-slate-500">{n.message || n.body}</p>
                <p className="mt-1 text-[10px] text-slate-400">{fmtDateTime(n.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useOutside(() => setOpen(false));
  return (
    <div className="relative" ref={ref}>
      <button data-testid="user-menu-button" onClick={() => setOpen(!open)}
        type="button"
        aria-label="User menu"
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex min-h-10 items-center gap-2 rounded-2xl border border-[#17211D]/10 bg-[#FFFAF1] p-1 shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] focus-visible:ring-offset-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#254F43] text-sm font-semibold text-[#FFFAF1]">
          {(user?.full_name || user?.email || "?")[0].toUpperCase()}
        </span>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      {open && (
        <div role="menu" className="tp-paper absolute right-0 z-50 mt-2 w-56 rounded-3xl py-1">
          <div className="border-b border-[#17211D]/10 px-4 py-2">
            <p className="truncate text-sm font-medium text-[#1A2420]">{user?.full_name}</p>
            <p className="truncate text-xs text-slate-500">{user?.email}</p>
          </div>
          <a
            href={supportEmailHref(user)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="help-support-link"
            onClick={(event) => { openSupportEmail(event, user); setOpen(false); }}
            role="menuitem"
            className="flex min-h-10 w-full items-center gap-2 border-b border-[#17211D]/10 px-4 py-2 text-left text-sm font-medium text-[#254F43] hover:bg-[#E7F2ED] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]"
          >
            <CircleHelp className="h-4 w-4" /> Help & support
          </a>
          <Link to="/account/settings" role="menuitem" onClick={() => setOpen(false)} className="flex min-h-10 items-center gap-2 px-4 py-2 text-sm font-medium text-[#254F43] hover:bg-[#E7F2ED] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]"><Settings className="h-4 w-4 shrink-0" /> Settings</Link>
          <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }}
            type="button"
            role="menuitem"
            className="flex min-h-10 w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-rose-600 hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function AppLayout({ children, allowViewportSticky = false }) {
  const loc = useLocation();
  const { perms, activeOrg, organizationProfile } = useAuth();
  const [organizationName, setOrganizationName] = useState("Your organization");
  const [organizationLogo, setOrganizationLogo] = useState("");
  const loadOrganizationName = useCallback(async () => {
    try {
      const { data } = await api.get("/org/profile");
      const nextName = data.profile?.display_name || data.organization?.name || "Your organization";
      setOrganizationName(nextName);
      setOrganizationLogo(data.profile?.logo || "");
    } catch {
      const { data } = await api.get("/orgs");
      const current = (data.data || []).find((org) => org.id === activeOrg) || (data.data || [])[0];
      setOrganizationName(current?.name || "Your organization");
      setOrganizationLogo("");
    }
  }, [activeOrg]);
  useEffect(() => {
    if (!organizationProfile) return;
    setOrganizationName(organizationProfile.display_name || "Your organization");
    setOrganizationLogo(organizationProfile.logo || "");
  }, [organizationProfile]);
  useEffect(() => {
    let cancelled = false;
    loadOrganizationName()
      .catch(() => {
        if (!cancelled) setOrganizationName("Your organization");
      });
    return () => { cancelled = true; };
  }, [loadOrganizationName]);
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const detail = event.detail || {};
      if (detail.orgId && activeOrg && detail.orgId !== activeOrg) return;
      setOrganizationName(detail.displayName || detail.organizationName || "Your organization");
      if (Object.prototype.hasOwnProperty.call(detail, "logo")) setOrganizationLogo(detail.logo || "");
    };
    window.addEventListener("trailpulse:organization-profile-updated", handleProfileUpdate);
    return () => window.removeEventListener("trailpulse:organization-profile-updated", handleProfileUpdate);
  }, [activeOrg]);
  const nav = [
    { to: "/trips", label: "Trips", icon: Map },
    ...(perms.includes("trip:create") ? [{ to: "/ai/plan", label: "AI Planner", icon: Sparkles }] : []),
    { to: "/flight-deals", label: "Flight deals", icon: Plane },
    ...(["org:manage", "participant:manage", "trip:edit"].some((p) => perms.includes(p)) ? [{ to: "/organization/settings", label: "Organization", icon: Building2 }] : []),
    { to: "/subscription", label: "Manage subscription", icon: CreditCard },
  ];
  return (
    <div className="tp-app-shell font-[Inter,sans-serif] text-[#17211D]">
      <header className="sticky top-0 z-40 mx-auto flex min-h-[64px] max-w-[1440px] items-center justify-between gap-3 px-3 py-2 sm:px-5 xl:px-6">
        <div className="tp-glass flex min-h-[48px] w-full items-center justify-between gap-3 rounded-[1.35rem] px-3 py-1.5">
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <Link to="/trips" aria-label={`${organizationName} trips`} className="flex min-h-10 min-w-0 items-center gap-2 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] focus-visible:ring-offset-2">
            <OrganizationBrandMark logo={organizationLogo} />
            <span className="hidden min-w-0 sm:block">
              <span className="block max-w-[240px] truncate font-[Plus_Jakarta_Sans,sans-serif] text-base font-extrabold leading-none tracking-tight">{organizationName}</span>
              <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#516259]">Operations workspace</span>
            </span>
          </Link>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <NotificationBell />
          <UserMenu />
        </div>
        </div>
      </header>
      <nav className="mx-auto mb-3 flex max-w-[1440px] gap-2 overflow-x-auto px-3 sm:px-5 md:hidden" aria-label="Mobile navigation">
        {nav.map((n) => {
          const active = loc.pathname.startsWith(n.to);
          return (
            <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className={`flex min-h-11 shrink-0 items-center gap-2 rounded-2xl px-4 text-sm font-extrabold shadow-sm ${active ? "bg-[#E5B85C] text-[#17211D]" : "bg-[#FFFAF1]/90 text-[#254F43]"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </Link>
          );
        })}
      </nav>
      <div className="mx-auto flex max-w-[1440px] gap-3 px-3 pb-4 sm:px-5 xl:px-6">
        <aside className="tp-dark-panel sticky top-[72px] hidden h-[calc(100svh-88px)] w-56 shrink-0 flex-col rounded-[1.35rem] p-3 text-[#FFFAF1] md:flex">
          <p className="mb-2 px-3 text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#FFFAF1]/50">Navigation</p>
          {nav.map((n) => {
            const active = loc.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={`mb-1 flex min-h-10 items-center gap-2.5 rounded-xl px-3 py-1.5 text-[13px] font-bold transition ${active ? "bg-[#E5B85C] text-[#17211D]" : "text-[#FFFAF1]/75 hover:bg-white/10 hover:text-white"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </Link>
            );
          })}
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/10 p-3">
            <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#E5B85C]"><Compass className="h-3.5 w-3.5" /> Atlas Mode</p>
            <p className="mt-2 text-[13px] leading-5 text-[#FFFAF1]/70">Travel-first planning surfaces with route, readiness, and field operations in one workspace.</p>
          </div>
          <div className="mt-3 rounded-2xl border border-white/10 bg-white/10 p-3">
            <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#E5B85C]"><Route className="h-3.5 w-3.5" /> Live Ops</p>
            <p className="mt-2 text-[13px] leading-5 text-[#FFFAF1]/70">Keep trips grounded in location, timing, packing, safety, and guide context.</p>
          </div>
          <div className="mt-auto pt-6 px-3 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#FFFAF1]/45">
            Powered by <span className="block pt-1 font-[Plus_Jakarta_Sans,sans-serif] text-sm normal-case tracking-normal text-[#FFFAF1]/75">TrailPulse</span>
          </div>
        </aside>
        <main id="main-content" className={`tp-page-surface min-h-[calc(100svh-92px)] flex-1 ${allowViewportSticky ? "min-w-0 overflow-x-clip" : "overflow-x-hidden"} rounded-[1.5rem] p-4 shadow-[0_24px_80px_rgba(23,33,29,0.14)] sm:p-5 lg:p-6`}>{children}</main>
      </div>
    </div>
  );
}
